import React, { useState, useEffect } from 'react';
import { Search, Save, Globe, Info, Sparkles, Image as ImageIcon } from 'lucide-react';
import { apiService, SEOSettings } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useToast } from '../contexts/ToastContext';

const SEOSettingsPage: React.FC = () => {
    const { showToast } = useToast();
    const [settings, setSettings] = useState<SEOSettings[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedPage, setSelectedPage] = useState<string>('home');
    const [currentSetting, setCurrentSetting] = useState<Partial<SEOSettings> | null>(null);
    const [ogImagePreview, setOgImagePreview] = useState<string | null>(null);

    const SEO_SUGGESTIONS: Record<string, Partial<SEOSettings>> = {
        home: {
            meta_title: 'BD Electric - Professional Electrical Services in Kathmandu',
            meta_description: 'BD Electric provides premium electrical services, installations, and repairs for homes and businesses in Kathmandu. Reliability and safety guaranteed.',
            meta_keywords: 'electrician kathmandu, electrical services nepal, home wiring, electrical repair, BD Electric',
            og_title: 'BD Electric | Expert Electrical Solutions in Kathmandu',
            og_description: 'Your trusted partner for all electrical needs in Kathmandu. Quality service, professional electricians.',
            schema_markup: {
                "@context": "https://schema.org",
                "@type": "LocalBusiness",
                "name": "BD Electric",
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Kathmandu, Nepal",
                    "addressLocality": "Kathmandu",
                    "addressRegion": "KTM",
                    "postalCode": "44600",
                    "addressCountry": "NP"
                }
            }
        },
        services: {
            meta_title: 'Our Electrical Services | BD Electric Kathmandu',
            meta_description: 'Explore our wide range of electrical services including residential wiring, commercial maintenance, backup power solutions, and emergency repairs.',
            meta_keywords: 'residential wiring, commercial electrical maintenance, generator installation, solar repair, electrical services',
            og_title: 'Professional Electrical Services - BD Electric',
            og_description: 'From minor repairs to major installations, our expert team handles it all with precision and safety.'
        },
        blog: {
            meta_title: 'Electrical Tips & News Blog | BD Electric',
            meta_description: 'Stay informed with our latest articles on electrical safety, energy-saving tips, and modern electrical technologies for your home and office.',
            meta_keywords: 'electrical safety tips, energy saving nepal, electrical blog, BD Electric news',
            og_title: 'BD Electric - Insights & Electrical Wisdom',
            og_description: 'Expert tips and advice to keep your home safe and efficient.'
        },
        contact: {
            meta_title: 'Contact Us | BD Electric - Get a Free Quote Today',
            meta_description: 'Get in touch with BD Electric for all your electrical inquiries. Call us, email us, or visit our office in Kathmandu for professional assistance.',
            meta_keywords: 'contact electrician, BD Electric phone number, electrician kathmandu address, request electrical quote',
            og_title: 'Need an Electrician? Contact BD Electric',
            og_description: 'Reach out to us for fast, reliable, and professional electrical services in Kathmandu.'
        },
        about: {
            meta_title: 'About BD Electric | Leading Electrical Contractors in Nepal',
            meta_description: 'With years of experience and a team of certified electricians, BD Electric delivers top-tier electrical installations and maintenance services across Nepal.',
            meta_keywords: 'about BD Electric, professional electricians nepal, electrical contractor kathmandu, our mission',
            og_title: 'The Team Behind BD Electric',
            og_description: 'Dedicated to lighting up Nepal with safety and excellence.'
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    useEffect(() => {
        if (settings.length > 0) {
            const setting = settings.find(s => s.page_type === selectedPage);
            setCurrentSetting(setting || { page_type: selectedPage as any });
            setOgImagePreview(setting?.og_image ? getImageUrl(setting.og_image) : null);
        } else if (!loading) {
            setCurrentSetting({ page_type: selectedPage as any });
            setOgImagePreview(null);
        }
    }, [selectedPage, settings, loading]);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const data = await apiService.getSeoSettings();
            setSettings(data);
        } catch (error) {
            console.error('Failed to fetch SEO settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAutoFill = () => {
        const suggestion = SEO_SUGGESTIONS[selectedPage];
        if (suggestion) {
            setCurrentSetting(prev => ({
                ...suggestion,
                id: prev?.id, // Preserve ID to ensure update instead of create
                page_type: selectedPage as any
            }));
            showToast('Premium suggestions loaded! Click "Save" to apply them.', 'info');
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setOgImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const formData = new FormData(form);

        // Validate Schema Markup JSON
        const schemaMarkup = formData.get('schema_markup') as string;
        if (schemaMarkup && schemaMarkup.trim()) {
            try {
                const parsed = JSON.parse(schemaMarkup);
                formData.set('schema_markup', JSON.stringify(parsed));
            } catch (err) {
                showToast('Invalid JSON in Schema Markup field. Please correct it before saving.', 'error');
                return;
            }
        }

        // Only include og_image if a new file was actually selected
        const fileInput = form.querySelector('input[name="og_image"]') as HTMLInputElement;
        if (fileInput && (!fileInput.files || fileInput.files.length === 0)) {
            formData.delete('og_image');
        }

        try {
            if (currentSetting?.id) {
                await apiService.updateSeoSetting(selectedPage, formData);
            } else {
                await apiService.createSeoSettings(formData);
            }
            showToast('SEO Settings saved successfully!', 'success');
            fetchSettings();
        } catch (error) {
            console.error('Failed to save SEO settings:', error);
            showToast('Error saving SEO settings. Please try again.', 'error');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-8 border-b pb-6">
                <h1 className="text-3xl font-bold text-gray-900">Search Engine Optimization</h1>
                <p className="text-gray-600 mt-2 font-medium italic select-none">Global meta tags and indexing control for main pages.</p>
            </div>

            <div className="flex gap-2 mb-8 bg-gray-100 p-1.5 rounded-2xl w-full overflow-x-auto whitespace-nowrap drop-shadow-sm scrollbar-hide">
                {['home', 'services', 'blog', 'contact', 'about'].map(page => (
                    <button
                        key={page}
                        onClick={() => setSelectedPage(page)}
                        className={`px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest transition-all whitespace-nowrap ${selectedPage === page ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        {page}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <input type="hidden" name="page_type" value={selectedPage} />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Meta Title</label>
                            <input
                                name="meta_title"
                                value={currentSetting?.meta_title || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, meta_title: e.target.value }))}
                                required
                                maxLength={60}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                                placeholder="Search result heading (max 60 chars)"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Meta Description</label>
                            <textarea
                                name="meta_description"
                                value={currentSetting?.meta_description || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, meta_description: e.target.value }))}
                                required
                                maxLength={160}
                                rows={3}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                                placeholder="Concise summary for search results (max 160 chars)"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Meta Keywords</label>
                            <input
                                name="meta_keywords"
                                value={currentSetting?.meta_keywords || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, meta_keywords: e.target.value }))}
                                required
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                                placeholder="comma, separated, keywords"
                            />
                        </div>

                        <div className="pt-4 border-t border-gray-50 md:col-span-2">
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <Globe className="h-4 w-4" /> Social Graph (Open Graph)
                            </h3>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">OG Title</label>
                            <input
                                name="og_title"
                                value={currentSetting?.og_title || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, og_title: e.target.value }))}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">OG Image</label>
                            <div className="flex items-center gap-4">
                                <div className="h-16 w-28 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0 group hover:border-orange-200 transition-colors">
                                    {ogImagePreview ? (
                                        <img src={ogImagePreview} alt="OG Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <ImageIcon className="h-6 w-6 text-gray-300" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        name="og_image"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="w-full text-[10px] text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 file:font-bold hover:file:bg-gray-200 cursor-pointer"
                                    />
                                    <p className="text-[10px] text-gray-400 mt-1 italic">Recommended: 1200x630px</p>
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">OG Description</label>
                            <textarea
                                name="og_description"
                                value={currentSetting?.og_description || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, og_description: e.target.value }))}
                                rows={2}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                            />
                        </div>

                        <div className="pt-4 border-t border-gray-50 md:col-span-2">
                            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                <Search className="h-4 w-4" /> Advanced Indexing
                            </h3>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Canonical URL</label>
                            <input
                                name="canonical_url"
                                placeholder="https://example.com/page"
                                value={currentSetting?.canonical_url || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, canonical_url: e.target.value }))}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Robots.txt Rules / Meta</label>
                            <input
                                name="robots_txt"
                                value={currentSetting?.robots_txt || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, robots_txt: e.target.value }))}
                                placeholder="index, follow"
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                Schema Markup (JSON-LD) <Info className="h-3 w-3 text-gray-400" />
                            </label>
                            <textarea
                                name="schema_markup"
                                value={typeof currentSetting?.schema_markup === 'object' ? JSON.stringify(currentSetting.schema_markup, null, 2) : currentSetting?.schema_markup || ''}
                                onChange={(e) => setCurrentSetting(prev => ({ ...prev, schema_markup: e.target.value }))}
                                rows={5}
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500/20 outline-none transition font-mono text-xs"
                                placeholder='{ "@context": "https://schema.org", ... }'
                            />
                        </div>
                    </div>

                    <div className="pt-6 border-t border-gray-100 flex justify-between items-center">
                        <button
                            type="button"
                            onClick={handleAutoFill}
                            className="text-indigo-600 hover:text-indigo-700 font-bold flex items-center gap-2 px-4 py-2 hover:bg-indigo-50 rounded-xl transition-all"
                        >
                            <Sparkles className="h-4 w-4" /> Use Premium Suggestions
                        </button>
                        <button
                            type="submit"
                            className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-orange-100 transition flex items-center gap-2"
                        >
                            <Save className="h-4 w-4" /> Save Configuration
                        </button>
                    </div>
                </form>
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-100 rounded-2xl p-6 flex gap-4">
                <Info className="h-6 w-6 text-blue-500 shrink-0" />
                <p className="text-sm text-blue-700 leading-relaxed">
                    <strong>Tip:</strong> Search engines typically refresh their index every few days. Changes saved here may take some time to reflect in live search results.
                </p>
            </div>
        </div>
    );
};

export default SEOSettingsPage;
